import { Component, OnInit , Input, OnChanges, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { formatDate } from '@telerik/kendo-intl';
import { SchedulerEvent } from '@progress/kendo-angular-scheduler';

import { SpinnerService } from '../../shared/spinner/spinner.service';
import {
  CategoryBase,
  MeetingType,
  SelectedSlotDto,
  MeetingRow,
  Language,
  AvailabilitySlotDto,
  MeetingDto,
  MeetingStatus } from '../../shared/model';
import { RescheduleViewService } from './reschedule-view.service';
import { SharedService } from '../../shared/shared.service';
import { TranslatedNotificationService } from '../../shared/translation/translated-notification.service';

import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-reschedule-view',
  templateUrl: './reschedule-view.component.html',
  styleUrls: [ '../../main-view/first-step/first-step.component.css',
    '../../main-view/second-step/second-step.component.css',
    '../../shared/styles/translation.css',
    '../../shared/styles/lang-color.css']
})
export class RescheduleViewComponent implements OnInit, OnChanges {
  @Input() existingMeeting: MeetingRow = <MeetingRow>{};
  @Input() availabilitySlots: Array<AvailabilitySlotDto>;
  @Output() changed: EventEmitter<boolean> = new EventEmitter();
  @Input() allLanguages: Array<Language>;
  @ViewChild('phoneInput', { static: false }) inputElement: ElementRef;
  @ViewChild('kendoScheduler', { static: false }) kendoScheduler: ElementRef;

  startTime = environment.schedulerStartHour;
  endTime = environment.schedulerEndHour;
  workDayStart = environment.workDayStartHour;
  workDayEnd = environment.workDayEndHour;
  events: SchedulerEvent[];
  isSlotSelected: boolean;
  selectedSlot: SelectedSlotDto = <SelectedSlotDto>{};
  meetingType: MeetingType = MeetingType.Teams;
  phoneRequired: boolean;
  englishLanguage: Language = <Language>{};
  currentLanguage: Language = <Language>{};
  telephoneForm = new FormGroup({
    codeNumber: new FormControl('', Validators.maxLength(3)),
    phoneNumber: new FormControl(''),
  });
  areTwoLanguagesSelected: boolean;

  private todaysDate: Date;
  private language: Language;

  constructor(
    
    private spinnerService: SpinnerService,
    private notificationService: TranslatedNotificationService,
    private sharedService: SharedService,
    private rescheduleService: RescheduleViewService) {
      this.currentLanguage = this.sharedService.translateLanguage(this.sharedService.getCurrentLanguage());
     }

  ngOnInit() {
    
    this.spinnerService.show('spinner');
    this.sharedService.getEnglishLanguage()
    .subscribe(result => {
      this.englishLanguage = this.sharedService.translateLanguage(result);
      this.spinnerService.hide('spinner');
      if (this.kendoScheduler) {
        const allDay = this.kendoScheduler.nativeElement.children[0].children[1].children[0].children[0].children;
        const allDayLeft = allDay[0].children[0].children[0].children[1];
        const allDayRight = allDay[1].children[0].children[1];
        allDayLeft.style.display = 'none';
        allDayRight.style.display = 'none';
      }
    },
    () => {
      this.spinnerService.hide('spinner');
      this.notificationService.showMsgError('error.loading_languages', 'Error while loading languages');
    });
  }

  ngOnChanges(changes) {

    if (changes.availabilitySlots && this.availabilitySlots !== undefined && this.currentLanguage !== undefined) {
      this.events = this.availabilitySlots
        .filter(x => x.LanguageId.toLowerCase() === this.currentLanguage.Id.toLowerCase())
        .map(dataItem => (
          <SchedulerEvent>{
            id: dataItem.Id,
            start: this.parseAdjust(dataItem.Start.toString()),
            end: this.parseAdjust(dataItem.End.toString()),
            isAllDay: false,
            title: dataItem.LanguageName,
            description: dataItem.CategoryName,
            roomId: dataItem.CategoryId,
            ownerID: dataItem.SupporterId
          }
        ));
    }

    if (changes.existingMeeting && !!this.existingMeeting) {
      this.telephoneForm.controls['phoneNumber'].setValue(this.existingMeeting.PhoneNumber);

      if (this.existingMeeting.Type === Number(MeetingType.Phone)) {
        this.setPhoneRadioButton();
      } else {
        this.setTeamsRadioButton();
      }
      this.telephoneForm.controls['phoneNumber'].updateValueAndValidity();
      this.selectedSlot.Type = MeetingType[this.meetingType];
      this.selectedSlot.MeetingType = this.meetingType;
    }
  }

  getFlagSrc(languageCode: any): string {
    if (languageCode) {
        const code = languageCode.toString().toUpperCase();
        return '../../../assets/GDPR/' + code + '.svg';
    }

    return '../../../assets/GDPR/EN.svg';
  }

  get selectedDate(): Date {
    return this.todaysDate;
  }

  englishAvailable(): boolean {
    return this.currentLanguage.LanguageCode.toString() !== 'en';
  }

  submitDisabled(): boolean {
    if (this.phoneRequired) {
      if (this.isSlotSelected && this.telephoneForm.valid) {
        return false;
      }
    } else if (this.isSlotSelected) {
      return false;
    }
    return true;
  }

  changedSelectedSlot(event, id: number, languageName: string) {

    if ($(event.target).hasClass('selected-orange')) {
      $('.event-div').removeClass('selected-orange');
      this.isSlotSelected = false;

    } else if ($(event.target).hasClass('event-div')) {
      $('.event-div').removeClass('selected-orange');
      $(event.target).addClass('selected-orange');

      this.setSelectedSlot(id, languageName);
      this.isSlotSelected = true;
    } else if ($(event.target).hasClass('selected-icon')) {
      $('.event-div').removeClass('selected-orange');
      $(event.target).parent().addClass('selected-orange');

      this.setSelectedSlot(id, languageName);
      this.isSlotSelected = true;
    }
    this.changed.emit(this.isSlotSelected);
  }

  radioButtonChange(event) {

    if (Number(event.target.value) === Number(MeetingType.Phone)) {
      this.setPhoneRadioButton();
    } else {
      this.setTeamsRadioButton();
    }
    this.telephoneForm.controls['phoneNumber'].updateValueAndValidity();
    this.selectedSlot.Type = MeetingType[this.meetingType];
    this.selectedSlot.MeetingType = this.meetingType;

    event.preventDefault();
  }

  languageChange(event, language: Language) {
    if (!event.target.checked) {
      this.areTwoLanguagesSelected = false;
      this.isSlotSelected = false;
      $('.event-div').removeClass('selected-orange');

      this.events = this.availabilitySlots
      .filter(x => x.LanguageId.toLowerCase() === this.currentLanguage.Id.toLowerCase())
      .map(dataItem => (
        <SchedulerEvent>{
          id: dataItem.Id,
          start: this.parseAdjust(dataItem.Start.toString()),
          startTimezone: null,
          end: this.parseAdjust(dataItem.End.toString()),
          endTimezone: null,
          isAllDay: false,
          title: dataItem.LanguageName,
          description: dataItem.CategoryName,
          recurrenceRule: null,
          recurrenceId: null,
          recurrenceException: null,
          roomId: dataItem.CategoryId,
          ownerID: dataItem.SupporterId
        }
      ));
    } else {
      this.areTwoLanguagesSelected = true;
      this.language = language;
      this.events = this.availabilitySlots
      .filter(x => x.LanguageId.toLowerCase() === this.language.Id.toLowerCase() || x.LanguageId.toLowerCase() === this.currentLanguage.Id.toLowerCase())
      .map(dataItem => (
        <SchedulerEvent>{
          id: dataItem.Id,
          start: this.parseAdjust(dataItem.Start.toString()),
          startTimezone: null,
          end: this.parseAdjust(dataItem.End.toString()),
          endTimezone: null,
          isAllDay: false,
          title: dataItem.LanguageName,
          description: dataItem.CategoryName,
          recurrenceRule: null,
          recurrenceId: null,
          recurrenceException: null,
          roomId: dataItem.CategoryId,
          ownerID: dataItem.SupporterId
        }
      ));
    }
  }

  submittedChange() {
    this.spinnerService.show('spinner');
    const phoneValues = this.telephoneForm.value;

    const meetingDto = <MeetingDto> {
      Id: this.existingMeeting.Id,
      AvailabilityId: this.selectedSlot.Id,
      LanguageId:  this.selectedSlot.Language.Id,
      CategoryId: this.selectedSlot.Category.Id,
      Type: this.selectedSlot.MeetingType,
      Keyword: this.existingMeeting.Keyword ?? '',
      MismatchedKeyword: this.existingMeeting.MismatchedKeyword ?? '',
      PhoneNumber: this.getPhoneNumber(phoneValues['codeNumber'], phoneValues['phoneNumber']),
      ProblemDescription: this.existingMeeting.ProblemDescription,
      AdditionalInformation: this.existingMeeting.AdditionalInformation ?? '',
      Status: MeetingStatus.Active
    };

  this.rescheduleService.rescheduleMeeting(meetingDto)
    .subscribe(result => {
      if (result) {
        this.spinnerService.hide('spinner');
        window.location.reload();
      }
    },
    (error) => {
      this.spinnerService.hide('spinner');
      this.notificationService.showMsgError('error.rescheduling', 'Error during rescheduling');
    });
  }

  formatHeaderDate(date: Date): string {
    return formatDate(date, 'dd/MM', 'en-US');
  }

  formatHeaderDateReturnDay(date: Date): string {
    return formatDate(date, 'EEE', 'en-US').toString();
  }

  dateTemplateString(date: Date): string {
   return 'scheduler_week_day_' + this.formatHeaderDateReturnDay(date).toString();
  }

  private setSelectedSlot(id: number, languageName: string) {
    const data = this.availabilitySlots.find(x => x.Id === id && x.LanguageName === languageName);

    this.selectedSlot.Id = data.Id;
    this.selectedSlot.Date = new Date(data.Start);
    this.selectedSlot.TimeSlot =  this.returnTimeSlot(this.parseAdjust(data.Start.toString()), this.parseAdjust(data.End.toString()));
    this.selectedSlot.Type = MeetingType[this.meetingType];
    this.selectedSlot.MeetingType = this.meetingType;
    this.selectedSlot.Language = this.sharedService.translateLanguage(<Language>{
      Id: data.LanguageId,
      Name: data.LanguageName
    });
    this.selectedSlot.Category = <CategoryBase>{
      Id: data.CategoryId,
      Name: data.CategoryName
    };
  }

  private parseAdjust = (eventDate: string): Date => {
    const date = new Date(eventDate + 'Z');
    return date;
  }

  private returnTimeSlot(start: Date, end: Date): string {
    return formatDate(start, 'HH:mm') + ' - ' + formatDate(end, 'HH:mm');
  }

  private getPhoneNumber(code: string, number: string): string {
    if (code !== '') {
      if (number !== '') {
        return '+' + code + ' ' + number;
      }
    } else {
        return number;
    }

    return '';
  }

  private setPhoneRadioButton() {
    this.meetingType = MeetingType.Phone;
    this.phoneRequired = true;
    this.telephoneForm.controls['phoneNumber'].setValidators([Validators.required, Validators.minLength(1)]);
    this.inputElement.nativeElement.focus();
  }

  private setTeamsRadioButton() {
    this.meetingType = MeetingType.Teams;
    this.phoneRequired = false;
    this.telephoneForm.controls['phoneNumber'].clearValidators();
  }

  setClassDependingOnLanguage(language: Language) : string {
    if (!language.LanguageCode) {
      return 'blue';
    } else {
        switch (language.LanguageCode.toString()) {
            case 'en':
                return 'blue';
            case 'fr':
                return 'green';
            case 'de':
                return 'orange';
            case 'es':
                return 'viola';
            case 'nl':
                return 'yellow';
            case 'it':
                return 'pink';
            default:
                return 'blue';
        }
    }
  }
}
